/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { getHeaders, handleResponse } from '@opengeoweb/api';
import type { Credentials } from '@opengeoweb/authentication';
import { sessionStorageProvider } from '@opengeoweb/shared';
import type {
  AirmetConfig,
  AirmetFromBackend,
  AirmetFromFrontend,
  AviationProduct,
  SigmetConfig,
  SigmetFromBackend,
  SigmetFromFrontend,
} from '../types';
import { i18n } from '../utils/i18n';

const getAirmetBaseUrl = (): string =>
  sessionStorageProvider.getConfig().GW_AIRMET_BASE_URL || '';
const getSigmetBaseUrl = (): string =>
  sessionStorageProvider.getConfig().GW_SIGMET_BASE_URL || '';

export const safeFetch = async <T>(
  url: string,
  options: RequestInit,
  timeoutMs = 5000,
): Promise<T> => {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return await handleResponse(response);
  } catch (err) {
    const newError: Error = {
      // eslint-disable-next-line @typescript-eslint/no-misused-spread
      ...(err as Error),
      name: i18n.t('error-backend'),
    };
    throw newError;
  } finally {
    clearTimeout(timeout);
  }
};

// API
export const getAirmetList = async (
  auth: Credentials,
): Promise<AirmetFromBackend[]> =>
  safeFetch(`${getAirmetBaseUrl()}/airmetlist`, {
    headers: getHeaders(auth),
  });

export const getSigmetList = async (
  auth: Credentials,
): Promise<SigmetFromBackend[]> =>
  safeFetch(`${getSigmetBaseUrl()}/sigmetlist`, {
    headers: getHeaders(auth),
  });

export const getAirmetConfig = async (
  auth: Credentials,
): Promise<AirmetConfig> => {
  return fetch(`${getAirmetBaseUrl()}/airmet-config`, {
    headers: getHeaders(auth),
  }).then(async (result) => result.json() as unknown as AirmetConfig);
};

export const getSigmetConfig = async (
  auth: Credentials,
): Promise<SigmetConfig> => {
  return fetch(`${getSigmetBaseUrl()}/sigmet-config`, {
    headers: getHeaders(auth),
  }).then(async (result) => result.json() as unknown as SigmetConfig);
};

const postTAC = async (
  url: string,
  auth: Credentials,
  product: AviationProduct,
): Promise<{ data: string }> => {
  const result = await fetch(url, {
    method: 'POST',
    body: JSON.stringify(product),
    headers: getHeaders(auth),
  });

  if (!result.ok) {
    throw new Error(`Failed to fetch TAC data: ${result.statusText}`);
  }

  const textData = await result.text();
  return { data: textData };
};

export const getSigmetTAC = async (
  auth: Credentials,
  product: AviationProduct,
): Promise<{ data: string }> =>
  postTAC(`${getSigmetBaseUrl()}/sigmet2tac`, auth, product);

export const getAirmetTAC = async (
  auth: Credentials,
  product: AviationProduct,
): Promise<{ data: string }> =>
  postTAC(`${getAirmetBaseUrl()}/airmet2tac`, auth, product);

export const postSigmet = async (
  auth: Credentials,
  data: SigmetFromFrontend,
): Promise<SigmetFromBackend> =>
  fetch(`${getSigmetBaseUrl()}/sigmet`, {
    headers: getHeaders(auth),
    method: 'POST',
    body: JSON.stringify(data),
  }).then(handleResponse) as Promise<SigmetFromBackend>;

export const postAirmet = async (
  auth: Credentials,
  data: AirmetFromFrontend,
): Promise<AirmetFromBackend> =>
  fetch(`${getAirmetBaseUrl()}/airmet`, {
    headers: getHeaders(auth),
    method: 'POST',
    body: JSON.stringify(data),
  }).then(handleResponse) as Promise<AirmetFromBackend>;
